import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Paper
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import ImageIcon from '@mui/icons-material/Image';

const ALLOWED_FILE_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

/**
 * Reusable image upload component with drag and drop support
 */
const ImageUpload = ({ onImageSelected, onImageRemoved, initialImage = null, uploading = false }) => {
  const { t } = useTranslation();
  const [preview, setPreview] = useState(initialImage);
  const [error, setError] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  // Sync preview with initialImage prop when it changes (e.g., when navigating between wizard steps)
  useEffect(() => {
    if (!initialImage) return;
    setPreview((previousPreview) => (initialImage !== previousPreview ? initialImage : previousPreview));
  }, [initialImage]);

  const validateFile = (file) => {
    setError('');

    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      setError(t('Invalid file type. Please upload PNG, JPG, or WebP'));
      return false;
    }

    if (file.size > MAX_FILE_SIZE) {
      setError(t('File size must be less than 5MB'));
      return false;
    }

    return true;
  };

  const handleFileSelect = (file) => {
    if (validateFile(file)) {
      // Create preview URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(file);

      // Notify parent component
      if (onImageSelected) {
        onImageSelected(file);
      }
    }
  };

  const handleFileInputChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleRemove = () => {
    setPreview(null);
    setError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (onImageRemoved) {
      onImageRemoved();
    }
  };

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <Box>
      {!preview ? (
        <Paper
          elevation={0}
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          sx={{
            border: (theme) => `2px dashed ${isDragging ? theme.palette.primary.main : theme.palette.divider}`,
            p: 4,
            textAlign: 'center',
            backgroundColor: (theme) => isDragging ?
              theme.palette.action.hover :
              theme.palette.background.default,
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            '&:hover': {
              borderColor: 'primary.main',
              backgroundColor: 'action.hover'
            }
          }}
          onClick={handleBrowseClick}
        >
          <ImageIcon sx={{ color: 'text.secondary', mb: 2 }} />
          <Typography variant="body1" gutterBottom>
            {t('Drag and drop an image here')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('or click to browse')}
          </Typography>
          <Button
            variant="outlined"
            startIcon={<CloudUploadIcon />}
            sx={{ mt: 2 }}
            disabled={uploading}
          >
            {t('Upload Image')}
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept={ALLOWED_FILE_TYPES.join(',')}
            onChange={handleFileInputChange}
            style={{ display: 'none' }}
          />
        </Paper>
      ) : (
        <Box>
          <Paper
            elevation={2}
            sx={{
              position: 'relative',
              overflow: 'hidden',
              maxWidth: 400,
              mx: 'auto'
            }}
          >
            <img
              src={preview}
              alt="Preview"
              style={{
                width: '100%',
                height: 'auto',
                display: 'block'
              }}
            />
            {uploading && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <CircularProgress />
              </Box>
            )}
          </Paper>
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleRemove}
              disabled={uploading}
            >
              {t('Remove image')}
            </Button>
          </Box>
        </Box>
      )}

      {uploading && (
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {t('Uploading...')}
          </Typography>
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}
    </Box>
  );
};

export default ImageUpload;
